import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopFeed, useIsMobile } from '../../hooks/useReaderCorner';

const C = { rust: '#B85C38', forest: '#2D4A3E', cream: '#F5F0E8', gold: '#d4a84b', grey: '#8B7355', ink: '#292524' };

function ensureUrl(url) {
  if (!url) return '#';
  return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
}

function Avatar({ username, avatar_url, size = 28, ring = '#fff' }) {
  const navigate = useNavigate();
  const initial = (username || '?').slice(0, 1).toUpperCase();
  return (
    <button
      onClick={() => username && navigate(`/u/${username}`)}
      style={{
        width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
        background: avatar_url ? `url(${avatar_url}) center/cover` : C.rust,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.42, fontWeight: 700, color: 'var(--brand-white)', fontFamily: 'var(--font-display)',
        border: `2px solid ${ring}`, cursor: username ? 'pointer' : 'default', padding: 'var(--space-none)',
      }}
    >
      {!avatar_url && initial}
    </button>
  );
}

function Handle({ username, color = C.grey }) {
  const navigate = useNavigate();
  if (!username) return null;
  return (
    <button onClick={() => navigate(`/u/${username}`)}
      style={{ background: 'none', border: 'none', padding: 'var(--space-none)', fontSize: 'var(--font-size-xs)', color, fontWeight: 500, cursor: 'pointer' }}>
      @{username}
    </button>
  );
}

const card = {
  background: '#fffdf8', border: `1px solid ${C.gold}66`, borderRadius: '26px 26px 14px 14px',
  boxShadow: '0 5px 20px rgba(45,74,62,0.07)', overflow: 'hidden',
};

function AwningStripe() {
  return (
    <div style={{
      height: 10,
      background: `repeating-linear-gradient(90deg, ${C.rust} 0px, ${C.rust} 12px, #fffdf8 12px, #fffdf8 24px)`,
    }} />
  );
}

function ShopCard({ shop, isMobile }) {
  return (
    <div style={card}>
      <AwningStripe />
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Avatar username={shop.username} avatar_url={shop.avatar_url} size={28} ring={C.gold} />
            <div>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: C.forest, margin: 'var(--space-none)' }}>
                🛍️ {shop.username ? `${shop.username}'s shop` : 'Shop'}
              </p>
              <Handle username={shop.username} />
            </div>
          </div>
          <span style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
            color: shop.platform === 'Etsy' ? '#be7c1e' : C.forest,
            border: `1px solid ${shop.platform === 'Etsy' ? '#be7c1e' : C.forest}66`,
            borderRadius: 'var(--radius-pill)', padding: '3px 10px',
          }}>
            {shop.platform}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 9, margin: '12px 0' }}>
          {shop.products.map(p => (
            <a key={p.id} href={ensureUrl(p.link)} target="_blank" rel="noopener noreferrer"
              style={{ background: '#faf6ec', borderRadius: 'var(--radius-lg)', padding: '13px 6px 10px', textAlign: 'center', border: `1px solid ${C.gold}44`, textDecoration: 'none', display: 'block' }}>
              {p.image_url ? (
                <div style={{ width: '100%', aspectRatio: '1/1', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 5 }}>
                  <img src={p.image_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ) : (
                <span style={{ fontSize: 26, display: 'block', marginBottom: 5 }}>{p.emoji || '📦'}</span>
              )}
              <p style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
              {p.price && <p style={{ fontSize: 11.5, fontWeight: 700, color: C.rust, margin: 'var(--space-none)' }}>{p.price.startsWith('$') ? p.price : `$${p.price}`}</p>}
            </a>
          ))}
          {Array.from({ length: Math.max(0, 4 - shop.products.length) }).map((_, i) => (
            <div key={`empty-${i}`} style={{ visibility: 'hidden', padding: '13px 6px 10px' }}>
              <div style={{ width: '100%', aspectRatio: '1/1' }} />
              <p style={{ fontSize: 10.5, margin: '0 0 2px' }}>&nbsp;</p>
            </div>
          ))}
        </div>

        {shop.shop_url && (
          <div style={{ textAlign: 'center' }}>
            <a href={ensureUrl(shop.shop_url)} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '8px 20px', background: C.forest, color: C.cream, border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, textDecoration: 'none' }}>
              Visit full shop →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function AmazonListCard({ item }) {
  return (
    <div style={card}>
      <AwningStripe />
      <div style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#146eb4', margin: '0 0 9px' }}>
          📦 a curated list · amazon storefront
        </p>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: C.forest, margin: '0 0 10px' }}>
          {item.list_icon} {item.list_name}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'center', marginBottom: 'var(--space-10)' }}>
          <Avatar username={item.username} avatar_url={item.avatar_url} size={22} />
          <Handle username={item.username} />
        </div>
        <a href={ensureUrl(item.url)} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', padding: '8px 20px', background: 'transparent', border: `1.5px solid ${C.forest}`, borderRadius: 'var(--radius-pill)', fontSize: 11.5, fontWeight: 700, color: C.forest, textDecoration: 'none' }}>
          Shop the list →
        </a>
      </div>
    </div>
  );
}

export default function ShopTab() {
  const { items, loading } = useShopFeed();
  const isMobile = useIsMobile();
  const shops       = items.filter(i => i.type === 'shop');
  const amazonLists = items.filter(i => i.type === 'amazon_list');

  if (loading && shops.length === 0 && amazonLists.length === 0) {
    return <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)' }}>Loading…</p>;
  }

  if (shops.length === 0 && amazonLists.length === 0) {
    return <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>No shops yet, be the first to add one.</p>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 16 : 22, alignItems: 'stretch' }}>
      {shops.map(shop => <ShopCard key={shop.user_id} shop={shop} isMobile={isMobile} />)}
      {amazonLists.map((item, i) => <AmazonListCard key={`${item.user_id}-${i}`} item={item} />)}
    </div>
  );
}